import { CameraEventListener } from '@/event/CameraEventListener';
import { DeviceRegistryEventListener } from '@/event/DeviceRegistryEventListener';
import { MicrophoneEventListener } from '@/event/MicrophoneEventListener';
import { ScreenEventListener } from '@/event/ScreenEventListener.ts';
import { BackgroundBlurrer } from '@/lib/blur/background-blurer';
import i18n from '@/lib/i18n';
import { manageError } from '@/lib/manage-error.ts';
import { useDeviceStore } from '@/store/DeviceStore.ts';
import { useSettingsStore } from '@/store/SettingsStore';
import intermind, {
  BackgroundBlur,
  BackgroundBlurMode,
  Camera,
  ExtendedDeviceRegistryListener,
  MediaDevice,
  MediaDeviceKind,
  Microphone,
  Speaker,
} from 'intermind';
import { MediaStream } from 'mind-sdk-web/lib/MediaStream';
import { toast } from 'sonner';

export interface DeviceService {
  // Setup
  setupAvailableDevices(): void;
  setupDeviceRegistry(): Promise<void>;
  useDefaultDevices(): Promise<void>;
  // Microphone
  useMicrophone(microphone: Microphone | null, saveSettings?: boolean): Promise<void>;
  useDefaultMicrophone(): Promise<void>;
  toggleMicrophone: () => Promise<void>;
  muteMicrophone: () => Promise<void>;
  // Camera
  useCamera(camera: Camera | null, saveSettings?: boolean): Promise<void>;
  useDefaultCamera(): Promise<void>;
  toggleCamera: () => Promise<void>;
  // Speaker
  useSpeaker(speaker: Speaker, saveSettings?: boolean): Promise<void>;
  // Media streams
  setupPrimaryMediaStream(): Promise<void>;
  setupSecondaryMediaStream(): Promise<void>;
  // Cleanup
  cleanup(): void;
}

export class DeviceServiceImpl implements DeviceService {
  private deviceRegistryListener: ExtendedDeviceRegistryListener | null = null;
  private backgroundBlurrer: BackgroundBlurrer | null = null;

  constructor() {
    this.deviceRegistryListener = new DeviceRegistryEventListener(this);
  }

  setupAvailableDevices(): void {
    const deviceRegistry = intermind.deviceRegistry;
    if (!deviceRegistry) {
      return;
    }

    useDeviceStore.getState().setAvailableMicrophones(deviceRegistry.getMicrophones());
    useDeviceStore.getState().setAvailableCameras(deviceRegistry.getCameras());
    useDeviceStore.getState().setAvailableSpeakers(deviceRegistry.getSpeakers() || []);
  }

  /**
   * Setup device registry listener, that triggers setupPrimaryMediaStream()
   * @throws Error if device initialization fails
   */
  async setupDeviceRegistry(): Promise<void> {
    // Set up device registry listener
    intermind.deviceRegistry?.setListener(this.deviceRegistryListener);

    // Update device registry
    await intermind.deviceRegistry?.update();
  }

  async useDefaultDevices(): Promise<void> {
    await this.useDefaultMicrophone();
    await this.useDefaultCamera();
    await this.useDefaultSpeaker();
  }

  async useDefaultMicrophone(): Promise<void> {
    console.log('Using default microphone');

    const defaulMicrophone = this.getDefaultDevice(MediaDeviceKind.Microphone);
    if (defaulMicrophone) {
      await this.useMicrophone(defaulMicrophone as Microphone, false);
    }
  }

  async useDefaultCamera(): Promise<void> {
    console.log('Using default camera');

    const defaulCamera = this.getDefaultDevice(MediaDeviceKind.Camera);
    if (defaulCamera) {
      await this.useCamera(defaulCamera as Camera, false);
    }
  }

  private async useDefaultSpeaker(): Promise<void> {
    console.log('Using default speaker');

    const defaulDevice = this.getDefaultDevice(MediaDeviceKind.Speaker);
    if (defaulDevice) {
      await this.useSpeaker(defaulDevice as Speaker, false);
    }
  }

  private getDefaultDevice(kind: MediaDeviceKind): MediaDevice | null {
    let availableDevices: MediaDevice[] | null = null;
    let settingsDeviceId: string | null = null;

    switch (kind) {
      case MediaDeviceKind.Microphone:
        availableDevices = useDeviceStore.getState().availableMicrophones;
        settingsDeviceId = useSettingsStore.getState().microphoneId;
        break;
      case MediaDeviceKind.Camera:
        availableDevices = useDeviceStore.getState().availableCameras;
        settingsDeviceId = useSettingsStore.getState().cameraId;
        break;
      case MediaDeviceKind.Speaker:
        availableDevices = useDeviceStore.getState().availableSpeakers;
        settingsDeviceId = useSettingsStore.getState().speakerId;
        break;
    }

    if (!availableDevices) return null;

    return (
      availableDevices.find((d) => d.getId() === settingsDeviceId) ||
      availableDevices.find((d) => d.isDefault()) ||
      availableDevices[0]
    );
  }

  async useMicrophone(newMicrophone: Microphone, saveSettings: boolean = true): Promise<void> {
    console.log(`Using microphone '${newMicrophone.getLabel()}', saveSettings: ${saveSettings}`);

    const prevMicrophone = useDeviceStore.getState().microphone;
    const microphoneEnabled = useDeviceStore.getState().microphoneEnabled;
    const microphoneChanged = prevMicrophone?.getId() !== newMicrophone?.getId();

    if (microphoneChanged) {
      useDeviceStore.getState().setMicrophone(newMicrophone);

      if (saveSettings) {
        useSettingsStore.getState().setMicrophoneId(newMicrophone?.getId());
      }
    }

    newMicrophone.setMuted(!microphoneEnabled);

    if (!newMicrophone.isAcquired()) {
      if (!newMicrophone.isAcquiring()) {
        await this.acquireMicrophone(newMicrophone);
        await this.setupPrimaryMediaStream();
      } else {
        console.warn(`Skip acquiring mic ${newMicrophone.getLabel()} because it's acquiring now`);
      }
    }

    if (microphoneChanged && prevMicrophone) {
      await this.releaseMicrophone(prevMicrophone);
    }
  }

  async toggleMicrophone(): Promise<void> {
    console.log('Toggling microphone');

    const currentDevice = useDeviceStore.getState().microphone;

    // This is for case when microphone is not acquired and user clicks toggle button
    if (!currentDevice) {
      await this.useDefaultMicrophone();
      return;
    }

    if (currentDevice.isAcquiring()) {
      console.warn(`Skip toggling mic '${currentDevice.getLabel()}' because it's acquiring now`);
      return;
    }

    if (!currentDevice.isAcquired()) {
      const succeeded = await this.acquireMicrophone(currentDevice);
      if (!succeeded) {
        // Update the model only if the operation completes successfully.
        // Otherwise, it's bad UX: if there's a persistent device capture error,
        // the UI button would still toggle regardless of the failure, which is misleading.
        return;
      }
    }

    const microphoneEnabled = useDeviceStore.getState().microphoneEnabled;
    currentDevice.setMuted(microphoneEnabled);
    useDeviceStore.getState().setMicrophoneEnabled(!microphoneEnabled);
  }

  async muteMicrophone(): Promise<void> {
    console.log('Muting microphone');

    const microphoneEnabled = useDeviceStore.getState().microphoneEnabled;
    if (microphoneEnabled) {
      await this.toggleMicrophone();
    }
  }

  async acquireMicrophone(microphone: Microphone, noiseSuppression: boolean = true): Promise<boolean> {
    console.log(
      `Acquiring microphone '${microphone.getLabel()}', noise suppression: ${noiseSuppression} in device svc`
    );

    const showMicrophoneInstructions = () => {
      console.warn('Microphone access is blocked');
      toast.error(i18n.t('device.microphoneAccessBlocked'), {
        duration: 5000,
      });
    };

    try {
      const eventListener = new MicrophoneEventListener(this);
      microphone.setListener(eventListener);
      await microphone.acquire();
      useDeviceStore.getState().setMicrophoneDenied(false);

      microphone.setNoiseSuppression(noiseSuppression);
      return true;
    } catch (error) {
      // On any capture error, the microphone must be explicitly disabled to ensure it doesn't accidentally turn on,
      // for example, after hardware changes.
      useDeviceStore.getState().setMicrophoneEnabled(false);
      if (error.name === 'NotAllowedError') {
        console.warn('Microphone permission denied');
        useDeviceStore.getState().setMicrophoneDenied(true);
        navigator.permissions
          .query({ name: 'microphone' as PermissionName })
          .then((permissionStatus) => {
            if (permissionStatus.state === 'denied') {
              showMicrophoneInstructions();
            }
          })
          .catch(() => {
            // If permissions API is not supported, show generic message
            showMicrophoneInstructions();
          });
      } else if (error.message?.includes('AudioWorkletNode')) {
        // If noise suppression fails, try to acquire the device without it
        console.warn('Noise suppression failed, falling back to basic microphone mode');
        return await this.acquireMicrophone(microphone, false);
      } else if (error.message?.includes('capture failure')) {
        // Handle MediaStreamTrack capture failures (common in PWA visibility changes)
        console.warn('MediaStreamTrack capture failure, this might be due to PWA visibility change');
        // Don't disable the microphone for capture failures, as they might be temporary
        return false;
      } else {
        manageError(error, i18n.t('errors.device.acquireMicrophone'));
        useDeviceStore.getState().setMicrophoneEnabled(false);
      }

      return false;
    }
  }

  async releaseMicrophone(microphone: Microphone): Promise<boolean> {
    console.log(`Releasing microphone '${microphone.getLabel()}' in device svc`);

    try {
      microphone.release();
      microphone.setListener(null);
      return true;
    } catch (error) {
      manageError(error, i18n.t('errors.device.releaseMicrophone'));
      return false;
    }
  }

  private setUpCameraConstraints(camera: Camera) {
    console.log(`Setting up camera constraints for '${camera.getLabel()}'`);

    if (camera) {
      const isMobileBrowser = navigator.maxTouchPoints > 1;
      camera.setResolution(isMobileBrowser ? 640 : 1280, isMobileBrowser ? 360 : 720);
      camera.setBitrate(isMobileBrowser ? 600_000 : 2520_000);
      camera.setAdaptivity(isMobileBrowser ? 2 : 3);
      camera.setFps(30);
    }
  }

  async useCamera(newCamera: Camera, saveSettings: boolean = true): Promise<void> {
    console.log(`Using camera '${newCamera.getLabel()}', saveSettings: ${saveSettings}`);

    const prevCamera = useDeviceStore.getState().camera;
    const cameraEnabled = useDeviceStore.getState().cameraEnabled;
    const cameraChanged = prevCamera?.getId() !== newCamera?.getId();

    if (cameraChanged) {
      useDeviceStore.getState().setCamera(newCamera);

      if (saveSettings) {
        useSettingsStore.getState().setCameraId(newCamera?.getId());
      }
    }

    if (cameraEnabled && !newCamera.isAcquired()) {
      if (!newCamera.isAcquiring()) {
        this.setUpCameraConstraints(newCamera);
        await this.acquireCamera(newCamera);
        await this.setupPrimaryMediaStream();
      } else {
        console.warn(`Skip acquiring camera ${newCamera.getLabel()} because it's acquiring now`);
      }
    }

    if (cameraChanged && prevCamera) {
      this.releaseCamera(prevCamera);
    }
  }

  async acquireCamera(device: Camera): Promise<boolean> {
    console.log(`Acquiring camera '${device.getLabel()}' in device svc`);

    const showCameraInstructions = () => {
      console.warn('Camera access is blocked');
      toast.error(i18n.t('device.cameraAccessBlocked'), {
        duration: 5000,
      });
    };

    try {
      const eventListener = new CameraEventListener(this);
      device.setListener(eventListener);
      await device.acquire();
      useDeviceStore.getState().setCameraDenied(false);
      return true;
    } catch (error) {
      // On any capture error, the camera must be explicitly disabled to ensure it doesn't accidentally turn on,
      // for example, after hardware changes.
      useDeviceStore.getState().setCameraEnabled(false);
      if (error.name === 'NotAllowedError') {
        console.warn('Camera permission denied');
        useDeviceStore.getState().setCameraDenied(true);
        navigator.permissions
          .query({ name: 'camera' as PermissionName })
          .then((permissionStatus) => {
            if (permissionStatus.state === 'denied') {
              showCameraInstructions();
            }
          })
          .catch(() => {
            // If permissions API is not supported, show generic message
            showCameraInstructions();
          });
      } else {
        manageError(error, i18n.t('errors.device.acquireCamera'));
        useDeviceStore.getState().setCameraEnabled(false);
      }
      return false;
    }
  }

  private releaseCamera(camera: Camera): boolean {
    console.log(`Releasing camera '${camera.getLabel()}' in device svc`);

    try {
      camera.release();
      camera.setListener(null);
      return true;
    } catch (error) {
      manageError(error, i18n.t('errors.device.releaseCamera'));
      return false;
    }
  }

  async toggleCamera(): Promise<void> {
    console.log('Toggling camera');

    const currentDevice = useDeviceStore.getState().camera;
    // This is for case when camera is not acuired and user clicks toggle button
    if (!currentDevice) {
      await this.useDefaultCamera();
      return;
    }

    if (currentDevice.isAcquiring()) {
      console.warn(`Skip toggling camera '${currentDevice.getLabel()}' because it's acquiring now`);
      return;
    }

    const cameraEnabled = useDeviceStore.getState().cameraEnabled;
    const succeeded = cameraEnabled ? this.releaseCamera(currentDevice) : await this.acquireCamera(currentDevice);
    if (succeeded) {
      // Update the model only if the operation completes successfully.
      // Otherwise, it's bad UX: if there's a persistent device capture error,
      // the UI button would still toggle regardless of the failure, which is misleading.
      useDeviceStore.getState().setCameraEnabled(!cameraEnabled);

      // Update the primary media stream because in case of background blur enabled we need to reset it
      await this.setupPrimaryMediaStream();
    }
  }

  async useSpeaker(speaker: Speaker, saveSettings: boolean = true): Promise<void> {
    console.log(`Using speaker '${speaker.getLabel()}', saveSettings: ${saveSettings}`);

    useDeviceStore.getState().setSpeaker(speaker);
    if (saveSettings) {
      useSettingsStore.getState().setSpeakerId(speaker?.getId());
    }
  }

  /**
   * Applies background blur to a media stream
   * @param primaryMediaStream The original media stream
   * @returns The blurred media stream or the original if blurring fails
   */
  private async applyBackgroundBlur(primaryMediaStream: MediaStream): Promise<MediaStream | null> {
    if (!primaryMediaStream || !BackgroundBlurrer.isSupported()) {
      return null;
    }

    try {
      // Create a blurrer if needed
      if (!this.backgroundBlurrer) {
        this.backgroundBlurrer = new BackgroundBlurrer();
      }

      const mode = useSettingsStore.getState().backgroundBlurMode;

      // Apply blur to the stream (BackgroundBlurrer is designed to work with the SDK's MediaStream)
      const blurredStream = await this.backgroundBlurrer.apply(
        primaryMediaStream,
        mode === BackgroundBlurMode.LOW_QUALITY_SEGMENTATION
      );
      return blurredStream || primaryMediaStream;
    } catch (error) {
      this.backgroundBlurrer?.dispose();
      this.backgroundBlurrer = null;
      toast.error(i18n.t('device.backgroundBlurFailed'));
      return null;
    }
  }

  /**
   * Sets up the primary media stream with current microphone and camera
   */
  async setupPrimaryMediaStream(): Promise<void> {
    const microphone = useDeviceStore.getState().microphone;
    const camera = useDeviceStore.getState().camera;

    console.log(
      `Setting up primary media stream with microphone: ${microphone?.getLabel()}, camera: ${camera?.getLabel()}`
    );
    if (!microphone && !camera) {
      console.warn('No microphone or camera found');
      return;
    }

    const primaryMediaStream = intermind.conference.createMediaStream(microphone, camera);

    // Apply background blur if enabled
    const backgroundBlurEnabled = useSettingsStore.getState().backgroundBlur === BackgroundBlur.ENABLED;
    if (primaryMediaStream && camera?.isAcquired() && backgroundBlurEnabled) {
      useDeviceStore.getState().setPrimaryMediaStreamBlurEnabling(true);
      const oldPrimaryMediaStream = useDeviceStore.getState().primaryMediaStream;
      if (!oldPrimaryMediaStream || !oldPrimaryMediaStream.hasVideoSupplier()) {
        useDeviceStore.getState().setPrimaryMediaStream(primaryMediaStream);
      }

      this.applyBackgroundBlur(primaryMediaStream)
        .then((bluredStream) => {
          if (bluredStream) {
            useDeviceStore.getState().setPrimaryMediaStream(bluredStream);
          } else {
            console.warn('Failed to apply background blur');
            toast.error('Failed to apply background blur.');
            useSettingsStore.getState().setBackgroundBlur(BackgroundBlur.DISABLED);
            useDeviceStore.getState().setPrimaryMediaStream(primaryMediaStream);
          }
        })
        .finally(() => useDeviceStore.getState().setPrimaryMediaStreamBlurEnabling(false));
    } else {
      useDeviceStore.getState().setPrimaryMediaStream(primaryMediaStream);
      this.backgroundBlurrer?.dispose();
      this.backgroundBlurrer = null;
    }
  }

  /**
   * Sets up the secondary media stream for screen sharing
   */
  async setupSecondaryMediaStream(): Promise<void> {
    const screen = intermind.deviceRegistry?.getScreen();
    if (!screen) {
      console.warn('No screen found for secondary media stream');
      return;
    }

    console.log(`Setting up secondary media stream with screen: ${!!screen}`);

    screen.setListener(new ScreenEventListener());
    useDeviceStore.getState().setScreen(screen);

    const secondaryMediaStream = intermind.conference.createMediaStream(screen, screen);
    useDeviceStore.getState().setSecondaryMediaStream(secondaryMediaStream);
  }

  /**
   * Cleans up devices
   */
  cleanup(): void {
    if (this.backgroundBlurrer) {
      this.backgroundBlurrer.dispose();
      this.backgroundBlurrer = null;
    }

    // Release all devices
    const camera = useDeviceStore.getState().camera;
    const microphone = useDeviceStore.getState().microphone;
    const screen = useDeviceStore.getState().screen;

    if (camera) {
      camera.release();
    }

    if (microphone) {
      microphone.release();
    }

    if (screen) {
      screen.release();
    }

    // Nullify references to media streams
    useDeviceStore.getState().setPrimaryMediaStream(null);
    useDeviceStore.getState().setSecondaryMediaStream(null);

    useDeviceStore.getState().microphone?.destroy();
    useDeviceStore.getState().camera?.destroy();
    useDeviceStore.getState().screen?.release();

    intermind.deviceRegistry?.setListener(null);
  }
}

export const deviceService = new DeviceServiceImpl();
